import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCapsule } from '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';

/**
 * Turns a catalogue silhouette key into geometry.
 *
 * This is the client's half of the catalogue: the contracts say a node is a `database`, and
 * this decides what a database looks like. Deliberately separate, because the contracts
 * assembly has zero dependencies and cannot name a mesh — and because a Unity client will make
 * its own choices here from the same keys.
 *
 * The vocabulary is chosen for **silhouettes that read at four metres**. This is a memory
 * palace, and spatial recall works on shape recognition; a wall of identical boxes told apart
 * only by their labels means reading the room to navigate it, which a flat diagram already does
 * better. Colour is the secondary channel, not the primary one.
 *
 * Everything here is built from primitives. No assets to fetch keeps cold start and offline
 * behaviour the same as the rest of the client.
 */

// spec: SPEC-084

/** Hand-sized, so a shape is something you reach out and take rather than push around. */
const SIZE = 0.3;

/**
 * Silhouettes drawn semi-transparent rather than solid.
 *
 * `external` is the odd one out in the catalogue: it is distinguished by *treatment* instead of
 * shape, because "a system that isn't ours" has no natural silhouette and inventing one nobody
 * recognises helps no-one. It composes correctly with being held, because NodeMeshFactory
 * records each mesh's resting alpha and the held look multiplies it rather than overwriting it.
 */
const GHOSTED = new Set(['ghost-box']);

const GHOST_ALPHA = 0.55;

export function isGhosted(silhouette: string): boolean {
  return GHOSTED.has(silhouette);
}

export function restingAlphaFor(silhouette: string): number {
  return isGhosted(silhouette) ? GHOST_ALPHA : 1;
}

/**
 * Builds the mesh for a silhouette.
 *
 * **One mesh per node is load-bearing.** Grab reach is measured against a single bounding
 * sphere, the held look sets alpha and emissive on a single `mesh.material`, and edges anchor
 * to a single position. So multi-part silhouettes are merged here rather than returned as a
 * parent with children — anything that hands back two meshes breaks possession, transparency
 * and edge anchoring at once.
 *
 * An unrecognised key returns a box. A client older than the catalogue then renders a
 * placeable, grabbable, if generic node instead of failing to render the room at all.
 */
export function buildSilhouette(silhouette: string, scene: Scene): Mesh {
  switch (silhouette) {
    case 'hex-prism':
      // Six sides reads as "service" the way a hexagon does on a flat diagram, and it is
      // unmistakable against the round database beside it.
      return CreateCylinder('node', { height: SIZE, diameter: SIZE, tessellation: 6 }, scene);

    case 'drum':
      return merge('node', scene, [
        CreateCylinder('drum-body', { height: SIZE * 0.82, diameter: SIZE * 0.82, tessellation: 20 }, scene),
        at(
          CreateCylinder('drum-top', { height: SIZE * 0.1, diameter: SIZE * 0.94, tessellation: 20 }, scene),
          0,
          SIZE * 0.44,
          0,
        ),
      ]);

    case 'slab-rack':
      // Three cards in a rack: a line of things waiting, which is what a queue is.
      return merge(
        'node',
        scene,
        [-1, 0, 1].map((column) =>
          at(
            CreateBox(
              `rack-${column}`,
              { width: SIZE * 0.2, height: SIZE * 0.85, depth: SIZE * 0.6 },
              scene,
            ),
            column * SIZE * 0.32,
            0,
            0,
          ),
        ),
      );

    case 'disc-stack':
      // Layers, stacked. Flat and wide where the database drum is tall and round, so the two
      // do not read as the same object at a glance.
      return merge(
        'node',
        scene,
        [-1, 0, 1].map((layer) =>
          at(
            CreateCylinder(
              `disc-${layer}`,
              { height: SIZE * 0.14, diameter: SIZE * 0.88, tessellation: 18 },
              scene,
            ),
            0,
            layer * SIZE * 0.26,
            0,
          ),
        ),
      );

    case 'figure':
      return merge('node', scene, [
        at(CreateCapsule('figure-body', { height: SIZE * 0.6, radius: SIZE * 0.15, tessellation: 10 }, scene), 0, -SIZE * 0.08, 0),
        at(CreateSphere('figure-head', { diameter: SIZE * 0.3, segments: 10 }, scene), 0, SIZE * 0.36, 0),
      ]);

    case 'sphere':
      // Low segment counts on purpose; at 30cm nobody sees the difference on a Quest.
      return CreateSphere('node', { diameter: SIZE, segments: 12 }, scene);

    case 'cylinder':
      return CreateCylinder('node', { height: SIZE, diameter: SIZE, tessellation: 16 }, scene);

    case 'cone':
      return CreateCylinder(
        'node',
        { height: SIZE, diameterTop: 0, diameterBottom: SIZE, tessellation: 16 },
        scene,
      );

    case 'capsule':
      return CreateCapsule('node', { height: SIZE, radius: SIZE / 3, tessellation: 12 }, scene);

    case 'box':
    case 'ghost-box':
    default:
      return CreateBox('node', { size: SIZE }, scene);
  }
}

function at(mesh: Mesh, x: number, y: number, z: number): Mesh {
  mesh.position.set(x, y, z);
  return mesh;
}

/**
 * Merges parts into one mesh, disposing the parts.
 *
 * A failed merge falls back to a plain box rather than to one of the parts: the parts may
 * already have been disposed by the attempt, and a generic node that works beats a fragment
 * that might not.
 */
function merge(name: string, scene: Scene, parts: Mesh[]): Mesh {
  const merged = Mesh.MergeMeshes(parts, true, true);

  if (!merged) {
    console.warn(`Could not merge silhouette parts for ${name}; falling back to a box.`);
    return CreateBox(name, { size: SIZE }, scene);
  }

  merged.name = name;

  return merged;
}
